import React, { useState, useCallback, useMemo } from 'react';
import { PetType } from '@spike/model';
import { Service, ServicePricing, FixedPriceOption, LateCheckoutFeeType, LATE_CHECKOUT_FEE_TYPES } from 'model/Service';
import useOrganizationMarketplaces from 'hooks/useOrganizationMarketplaces';
import { buildPricingTaxesFromMarketplaces } from 'components/Service/Pricing/utils/taxElements';
import styles from './BoardingPricingStep.module.scss';
import Button from 'components/UI/V3/Button/Button';
import { BoardingPricingTypeSelector } from './Sections/PricingType/BoardingPricingTypeSelector';
import { BoardingTaxesSelector } from './Sections/Taxes/BoardingTaxesSelector';
import { BoardingFixedPriceTable } from './Sections/PriceTable/BoardingFixedPriceTable';
import { LateCheckoutFeeConfig } from './Sections/LatePickup/LateCheckoutFeeConfig';
import { GracePeriodSection } from './Sections/LatePickup/GracePeriodSection';
import { MinNightsSection } from './Sections/Duration/MinNightsSection';

interface BoardingPricingStepProps {
    selectedPetTypes: Array<PetType>;
    marketplaces: Service['marketplaces'];
    existingService?: Service;
    onNext: (
        pricing: ServicePricing,
        boardingData: {
            dropoff24h: boolean;
            minNights: number;
            latePickupFee: {
                enabled: boolean;
                type?: LateCheckoutFeeType;
                gracePeriod: number;
                fees: Record<string, number>;
            };
        },
        marketplaces: Service['marketplaces']
    ) => void;
}

export const BoardingPricingStep: React.FC<BoardingPricingStepProps> = ({
    selectedPetTypes,
    marketplaces: initialMarketplaces,
    existingService,
    onNext
}) => {
    const { organizationServicesInfo } = useOrganizationMarketplaces();

    const [pricingType, setPricingType] = useState<'fixed' | 'by_pet_size'>(() => {
        if (existingService?.pricing?.fixedPrice && existingService.pricing.fixedPrice.length > 0) {
            return 'fixed';
        }
        return 'fixed';
    });

    const [taxable, setTaxable] = useState(
        () =>
            existingService?.pricing?.taxable ||
            initialMarketplaces.some(marketplace => marketplace.taxes.length > 0) ||
            false
    );
    const [marketplaces, setMarketplaces] = useState<Service['marketplaces']>(() => initialMarketplaces);
    const [fixedPrices, setFixedPrices] = useState<FixedPriceOption[]>(
        () => existingService?.pricing?.fixedPrice || []
    );

    const [isPriceValidationValid, setIsPriceValidationValid] = useState(true);
    const [priceValidationErrors, setPriceValidationErrors] = useState<Record<string, string>>({});

    // Boarding specific states
    const [minNights, setMinNights] = useState<number>(() => {
        return existingService?.boardingData?.minNights || 1;
    });

    const [lateCheckoutFee, setLateCheckoutFee] = useState<{
        enabled: boolean;
        type?: LateCheckoutFeeType;
        gracePeriod: number;
    }>(() => {
        return {
            enabled: existingService?.boardingData?.latePickupFee?.enabled || false,
            type: existingService?.boardingData?.latePickupFee?.type || LATE_CHECKOUT_FEE_TYPES.FIXED_FEE,
            gracePeriod: existingService?.boardingData?.latePickupFee?.gracePeriod || 30
        };
    });

    const [lateCheckoutFees, setLateCheckoutFees] = useState<Record<string, number>>(() => {
        return existingService?.boardingData?.latePickupFee?.fees || {};
    });

    // Keep marketplace list in sync when locations change in a previous step
    React.useEffect(() => {
        setMarketplaces(prev =>
            initialMarketplaces.map(marketplace => {
                const existing = prev.find(mp => mp.marketplaceId === marketplace.marketplaceId);
                return {
                    ...marketplace,
                    taxes: existing?.taxes ?? marketplace.taxes,
                    staffIds: existing?.staffIds ?? marketplace.staffIds,
                    roomIds: marketplace.roomIds ?? existing?.roomIds ?? [],
                    roomUnitIds: marketplace.roomUnitIds ?? existing?.roomUnitIds ?? [],
                    vehicleIds: marketplace.vehicleIds ?? existing?.vehicleIds ?? []
                };
            })
        );
    }, [initialMarketplaces]);

    const taxes = useMemo(
        () => buildPricingTaxesFromMarketplaces(marketplaces, organizationServicesInfo),
        [marketplaces, organizationServicesInfo]
    );

    const handlePriceValidationChange = useCallback((isValid: boolean, errors: Record<string, string>) => {
        setIsPriceValidationValid(isValid);
        setPriceValidationErrors(errors);
    }, []);

    const hasAllRoomsAssigned = useMemo(
        () => marketplaces.length > 0 && marketplaces.every(marketplace => (marketplace.roomIds ?? []).length > 0),
        [marketplaces]
    );

    const isValid = useMemo(() => {
        if (!hasAllRoomsAssigned) return false;

        if (pricingType === 'fixed') {
            const hasValidBasicPrices =
                fixedPrices.length > 0 &&
                fixedPrices.every(price => price.price !== undefined && price.price !== null && price.price >= 0);
            if (!hasValidBasicPrices) return false;

            if (lateCheckoutFee.enabled) {
                const selectedPetTypeIds = selectedPetTypes.map(pt => pt.id);
                const hasValidLateCheckoutFees = selectedPetTypeIds.every(petTypeId => {
                    const fee = lateCheckoutFees[petTypeId];
                    return fee && fee > 0;
                });

                if (!hasValidLateCheckoutFees) return false;
            }
        }

        if (taxable && taxes.length === 0) return false;

        if (!isPriceValidationValid) return false;

        if (minNights <= 0) return false;

        return true;
    }, [
        hasAllRoomsAssigned,
        pricingType,
        fixedPrices,
        lateCheckoutFee,
        lateCheckoutFees,
        selectedPetTypes,
        taxable,
        taxes,
        isPriceValidationValid,
        minNights
    ]);

    const handleNext = useCallback(() => {
        if (!isValid) return;

        const pricing: ServicePricing = {
            taxable,
            taxes,
            fixedPrice: pricingType === 'fixed' ? fixedPrices : [],
            variablePrice: pricingType === 'by_pet_size' ? undefined : undefined
        };

        const boardingData = {
            dropoff24h: existingService?.boardingData?.dropoff24h || false,
            minNights: minNights,
            latePickupFee: {
                enabled: lateCheckoutFee.enabled,
                type: lateCheckoutFee.type,
                gracePeriod: lateCheckoutFee.gracePeriod,
                fees: lateCheckoutFees
            }
        };

        onNext(pricing, boardingData, marketplaces);
    }, [
        pricingType,
        taxable,
        taxes,
        fixedPrices,
        minNights,
        lateCheckoutFee,
        lateCheckoutFees,
        marketplaces,
        isValid,
        onNext,
        existingService?.boardingData?.dropoff24h
    ]);

    return (
        <div className={styles.container}>
            <div className={styles.content}>
                <div className={styles.section}>
                    <BoardingPricingTypeSelector
                        value={pricingType}
                        onChange={setPricingType}
                    />
                </div>

                <div className={styles.separator}></div>

                <div className={styles.section}>
                    <div className={styles.optionsBlock}>
                        <div className={styles.optionSection}>
                            <LateCheckoutFeeConfig
                                lateCheckoutFee={lateCheckoutFee}
                                onLateCheckoutFeeChange={setLateCheckoutFee}
                            />
                        </div>

                        <div className={styles.optionSection}>
                            {lateCheckoutFee.enabled && (
                                <GracePeriodSection
                                    value={lateCheckoutFee.gracePeriod}
                                    onChange={gracePeriod => setLateCheckoutFee(prev => ({ ...prev, gracePeriod }))}
                                />
                            )}
                        </div>
                    </div>
                </div>

                <div className={styles.section}>
                    <BoardingFixedPriceTable
                        selectedPetTypes={selectedPetTypes}
                        fixedPrices={fixedPrices}
                        onFixedPricesChange={setFixedPrices}
                        showLateCheckoutColumn={lateCheckoutFee.enabled}
                        lateCheckoutFeeType={lateCheckoutFee.type}
                        lateCheckoutFees={lateCheckoutFees}
                        onLateCheckoutFeesChange={setLateCheckoutFees}
                        onValidationChange={handlePriceValidationChange}
                    />
                </div>

                <div className={styles.section}>
                    <BoardingTaxesSelector
                        taxable={taxable}
                        marketplaces={marketplaces}
                        onTaxableChange={setTaxable}
                        onChangeMarketplaces={setMarketplaces}
                    />
                </div>

                <div className={styles.section}>
                    <MinNightsSection
                        value={minNights}
                        onChange={setMinNights}
                    />
                </div>

                <div className={styles.separator}></div>
            </div>

            <div className={styles.buttonContainer}>
                <Button
                    label="Save"
                    variant="primary"
                    onClick={handleNext}
                    disabled={!isValid}
                    className={styles.saveButton}
                />
                {!hasAllRoomsAssigned && (
                    <span className={styles.roomsRequiredMessage}>
                        All selected location must have a room assigned.
                    </span>
                )}
            </div>
        </div>
    );
};
